/**
 * @author: lv.chen
 * @date: 2025/2/18
 * @desc: Pipeline/Change Log页面Change Checklist页签
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Justify, Card, PagingQuery } from 'tea-component';
import MultiTypeDownloadBtn from '@intelligence/pages/TopCharts/components/MultiTypeDownloadBtn';
import SearchCommon from '@gp/components/SearchCommon';
import ChangeChecklistFilter from './components/ChangeChecklistFilter';
import ChangeChecklistTable from './components/ChangeChecklistTable';
import { CHANGE_CHECKLIST_DATE_TYPES } from './constants';
import useRequest from '@gp/hooks/useRequest';
import usePageLog from '@gp/hooks/usePageLog';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import usePageFilterInit from '@gp/hooks/usePageFilterInit';
import useDomainPageStore from './_hooks/useDomainPageStore';
import {
  fetchChangeChecklists,
  downloadChangeChecklists,
} from '@gp/services/changeLog';
import { fetchCommonConditions } from '@gp/services/filter';
import { getSearchList } from './services';
import {
  toProjectDetails,
  formatSearchProjectsResults,
} from '@gp/utils/project';
import { formatSearchParams } from '@gp/pages/Pipeline/utils';
import {
  formatFilterConditions,
  formatFilterFetchParams,
  formatSortFetchParams,
} from './utils';
import { DEFAULT_FILTER, DEFAULT_SORT } from './models/locker';
import {
  FILE_TYPES_MAP,
  SIMPLE_DOWNLOAD_FILE_TYPES1,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGINATION,
  DEFAULT_SEARCH,
} from '@gp/constants/const';
import { GRANULARITY_MAPPINGS, getMonthFromtTime } from '@gp/utils/date';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type {
  IRecord,
  ISearchPayload,
  ISearchPayloadPageStore,
} from '@gp/types/global';
import type {
  IPipelineChangeChecklistFilterConditionsVO,
  IPipelineChangeChecklistFilterVO,
  IPipelineChangeChecklistRecordVO,
} from '@gp/types/changeLog';
import './index.scss';
import { deleteFilterAllValue } from '@gp/utils/utils';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';

type IChangeChecklistProps = IBaseProps;

const ChangeChecklist: React.FC<IChangeChecklistProps> = (props) => {
  const { style, className, headerTitle, scrollBody } = props;
  const currentPage = 'pipelineChangeLogChangeChecklist';
  const showRightBtn = true;
  const commonCondParams = {
    change_check_list_time_range: [],
    pteam_bo_studio_project: {
      project_auth: false,
      sensitive_filter_auth: true,
    },
    priority: [],
    stage: [],
  };
  const [selectedValue, setSelectedValue] = useState<any>({});
  const { loading: filterConditionsLoading, data: filterConditionsRes } =
    useRequest(fetchCommonConditions, {
      defaultParams: [commonCondParams],
    });

  const {
    values,
    conditions: filterConditions,
    defaultValues: allValuesInterface,
  } = formatFilterConditions(filterConditionsRes);

  const {
    lock,
    filter: lockerFilter,
    sort: lockerSort,
    pagination: lockerPagination,
    search: lockerSearch,
  } = useDomainPageStore({
    getFilter: () => filter,
    getSort: () => sort,
    getPagination: () => pagination,
    getSearch: () => search,
  });

  const fields = [
    {
      key: 'rangePicker',
      type: 'rangePicker',
      saveKey: 'checklist_rangePicker',
    },
    {
      key: KEYS_MAPPING.CASCADE_PROJECT,
      type: 'cascadeProject',
      label: NAMES_MAPPING.CASCADE_PROJECT,
    },
    { key: 'priority', type: 'selectMultiple', label: 'Priority' },
    { key: 'stage', type: 'selectMultiple', label: 'Stage' },
  ];

  const {
    loading: initialLoading,
    filter: initialFilter,
    onUpdateShortUrlParams,
  } = usePageFilterInit({
    loading: filterConditionsLoading,
    conditions: {
      filter: filterConditions,
    },
    storeValues: {
      lock,
      filter: lockerFilter,
    },
    defaultValues: {
      filter: {
        ...DEFAULT_FILTER,
        rangePicker: {
          ...DEFAULT_FILTER.rangePicker,
          ...values?.rangePicker,
          typeList: CHANGE_CHECKLIST_DATE_TYPES,
        },
        project_id: values?.project_id || [],
      },
    },
    allFilterOptionsValues: allValuesInterface,
    setSelectedValue,
    fields,
  });

  const defaultValuesCur = useMemo(() => {
    return {
      ...DEFAULT_FILTER,
      rangePicker: {
        ...DEFAULT_FILTER.rangePicker,
        ...values?.rangePicker,
        typeList: CHANGE_CHECKLIST_DATE_TYPES,
      },
      project_id: values?.project_id || [],
    };
  }, [DEFAULT_FILTER, CHANGE_CHECKLIST_DATE_TYPES, values]);

  const [tableHeight, setTableHeight] = useState(0);
  const filterHeightRef = useRef(null);
  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey:
      'PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST_DOWNLOAD_CSV_SUBMIT',
    excelDownloadLogKey:
      'PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST_DOWNLOAD_EXCEL_SUBMIT',
    downloadService: createDownloadService(downloadChangeChecklists),
  });

  const [filter, setFilter] = useState<IPipelineChangeChecklistFilterVO>(
    initialFilter as any,
  );
  const [sort, setSort] = useState<SortBy<IPipelineChangeChecklistRecordVO>[]>(
    lock ? lockerSort : DEFAULT_SORT,
  );

  const [pagination, setPagination] = useState<PagingQuery>(
    lock ? lockerPagination : DEFAULT_PAGINATION,
  );

  const [search, setSearch] = useState<ISearchPayloadPageStore>(
    lock ? lockerSearch : DEFAULT_SEARCH,
  );

  const isSearch = !!search?.search_keyword && search?.search?.length > 0;

  const [changeChecklistsLoading, setChangeChecklistsLoading] = useState(true);
  const [changeChecklistsError, setChangeChecklistsError] = useState<
  Error | undefined
  >(undefined);
  const [changeChecklistsTotal, setChangeChecklistsTotal] = useState(0);
  const [changeChecklists, setChangeChecklists] = useState<
  IPipelineChangeChecklistRecordVO[]
  >([]);

  const updateChangeChecklists = async (
    filterPayload: IPipelineChangeChecklistFilterVO = filter,
    filterConditionsPayload: IPipelineChangeChecklistFilterConditionsVO = filterConditions,
    sortPayload: SortBy<IPipelineChangeChecklistRecordVO>[] = sort,
    paginationPayload: PagingQuery = pagination,
    searchPayload: ISearchPayloadPageStore = search,
  ) => {
    try {
      const filterTmp = { ...filter, ...filterPayload };
      let filterParams = formatFilterFetchParams(
        filterTmp,
        filterConditionsPayload,
      );
      const sortParams = formatSortFetchParams(sortPayload);
      const searchParams = formatSearchParams(searchPayload);
      const { pageIndex, pageSize } = paginationPayload || {};

      // 若继承到了,非月粒度的时间粒度，则需要转回默认月粒度
      if (
        GRANULARITY_MAPPINGS[filterPayload?.rangePicker?.dateType] === 'day'
      ) {
        filterParams = {
          ...filterParams,
          granularity: 'month',
          start_date: getMonthFromtTime(0),
          end_date: getMonthFromtTime(1),
        };
      }

      const params = {
        ...filterParams,
        ...sortParams,
        ...searchParams,
        page_num: pageIndex || 1,
        page_size: pageSize || DEFAULT_PAGE_SIZE,
      };

      setFilter(filterTmp);
      setChangeChecklistsLoading(true);
      const res = await fetchChangeChecklists(params);
      const { total, list } = res?.data || {};
      setChangeChecklistsTotal(total || 0);
      setChangeChecklists(list || []);
      setChangeChecklistsError(undefined);
    } catch (exception: any) {
      setChangeChecklistsTotal(0);
      setChangeChecklists([]);
      setChangeChecklistsError(exception);
    } finally {
      setChangeChecklistsLoading(false);
    }
  };

  const handleFilterChange = (payload?: IPipelineChangeChecklistFilterVO) => {
    const filterTmp = { ...filter, ...payload };
    const paginationTmp = { ...pagination, pageIndex: 1 };
    setFilter(filterTmp);
    setPagination(paginationTmp);
    updateChangeChecklists(filterTmp, filterConditions, sort, paginationTmp);
    onUpdateShortUrlParams({ filter: filterTmp });
  };

  const handleSortChange = (
    payload?: SortBy<IPipelineChangeChecklistRecordVO>[],
  ) => {
    const firstSort = payload?.[0];
    const payloadTmp = !!firstSort ? [firstSort] : [];
    const paginationTmp = { ...pagination, pageIndex: 1 };
    setSort(payloadTmp);
    setPagination(paginationTmp);
    updateChangeChecklists(filter, filterConditions, payloadTmp, paginationTmp);
  };

  const handlePaginationChange = (payload?: PagingQuery) => {
    const paginationTmp = { ...pagination, ...payload };
    setPagination(paginationTmp);
    updateChangeChecklists(filter, filterConditions, sort, paginationTmp);
  };

  const handleSearchSubmit = (
    payload: ISearchPayload,
    searchList: IRecord<any>[],
  ) => {
    const {
      search_type: searchType,
      search_keyword: searchKeyword,
      search,
      search_ids: searchIds,
    } = payload || {};
    const paginationTmp = { ...pagination, pageIndex: 1 };
    const searchTmp = {
      search_type: searchType || '',
      search_keyword: searchKeyword || '',
      search: search || [],
      search_ids: searchIds || [],
      search_list: searchList || [],
    };
    setSearch(searchTmp);
    setPagination(paginationTmp);
    updateChangeChecklists(
      filter,
      filterConditions,
      sort,
      paginationTmp,
      searchTmp,
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRetry = () => {
    updateChangeChecklists();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReset = () => {
    // const filterTmp = { ...filter, ...values };
    const filterTmp = { ...defaultValuesCur } as any;
    const paginationTmp = { ...pagination, pageIndex: 1 };
    const searchTmp = lock ? lockerSearch : DEFAULT_SEARCH;
    setFilter(filterTmp);
    setPagination(paginationTmp);
    setSearch(searchTmp);
    updateChangeChecklists(
      filterTmp,
      filterConditions,
      sort,
      paginationTmp,
      searchTmp,
    );
    onUpdateShortUrlParams({ filter: filterTmp });
    setSelectedValue({});
    deleteFilterAllValue();
  };

  const setSearchListHandle = (list: any) => {
    const searchDataTmp = {
      ...search,
      search_list: list || [],
    };
    setSearch(searchDataTmp);
  };
  const setSearchValHandle = (val: any) => {
    const searchDataTmp = {
      ...search,
      search_keyword: val || '',
    };
    setSearch(searchDataTmp);
  };

  const handleDownloadInPage = (fileType?: string) => {
    const fileTypeTmp = fileType || FILE_TYPES_MAP.CSV;
    const filterParams = formatFilterFetchParams(filter, filterConditions);
    const sortParams = formatSortFetchParams(sort);
    const searchParams = formatSearchParams(search);
    handleDownload({
      ...filterParams,
      ...sortParams,
      ...searchParams,
      // REMARK: 下载时，page_num传0
      page_num: 0,
      // REMARK: 下载时，page_size传0
      page_size: 0,
      fileType: fileTypeTmp,
      file_type: fileTypeTmp,
    });
  };

  useEffect(() => {
    if (initialLoading) return;
    updateChangeChecklists(initialFilter as any, filterConditions);
    onUpdateShortUrlParams({ filter: initialFilter });
  }, [initialLoading]);

  usePageLog({ pageId: 'PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST' });

  // 刷新搜索组件弹出面板位置的依赖数组
  const searchSelectUpdateDeps = useMemo<React.DependencyList>(() => [], []);

  useEffect(() => {
    const handleResize = () => {
      const hValue = window.innerHeight;

      if (filterHeightRef.current) {
        const filterRefHeight =
          filterHeightRef.current?.getBoundingClientRect().height;
        let varHeight = 0;
        if (filterRefHeight === 46) {
          varHeight = 46;
        } else if (filterRefHeight === 36) {
          varHeight = 46;
        } else {
          varHeight = 92;
        }
        const valueH = hValue - varHeight - 243 - 78;
        setTableHeight(valueH);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filterConditionsLoading]);

  return (
    <div
      style={style}
      className={classnames(
        'dbg-pipeline-change-log-change-checklist dbg-pipeline-table-card-body-bottom0px',
        className,
      )}
    >
      <Justify
        className="dbg-common-page__toolbar-justify"
        right={
          <div className="dbg-common-page__toolbar">
            <SearchCommon
              placeholder="Search any project in the list"
              showRightBtn={showRightBtn}
              currentPage={currentPage}
              updateDeps={searchSelectUpdateDeps}
              submit={handleSearchSubmit}
              getSearchList={(payload: any) => {
                return getSearchList(payload, {
                  sort,
                });
              }}
              formatSearchResults={formatSearchProjectsResults}
              searchVal={search?.search_keyword}
              searchList={search?.search_list}
              onRightBtnClick={(payload: any, isJump = true) => {
                const { project_id: id, name } = payload || {};
                const params = { id, name, tab: 'gameDetail' };
                return toProjectDetails(params, null, isJump);
              }}
              setSearchList={setSearchListHandle}
              setSearchVal={setSearchValHandle}
            />
            <MultiTypeDownloadBtn
              isDisabled={changeChecklistsLoading || !changeChecklists?.length}
              isLoading={isDownloading}
              options={SIMPLE_DOWNLOAD_FILE_TYPES1}
              onSelect={handleDownloadInPage}
            />
          </div>
        }
      />
      <ChangeChecklistFilter
        loading={filterConditionsLoading}
        data={filterConditions}
        value={filter}
        onChange={handleFilterChange}
        allFilterOptionsValues={allValuesInterface}
        onReset={handleReset}
        scrollBody={scrollBody}
        headerTitle={headerTitle}
        currentPage={currentPage}
        updateDeps={searchSelectUpdateDeps}
        handleSearch={handleSearchSubmit}
        getSearchList={(payload: any) => {
          return getSearchList(payload, {
            sort,
          });
        }}
        formatSearchResults={formatSearchProjectsResults}
        searchVal={search?.search_keyword}
        searchList={search?.search_list}
        showRightBtn={showRightBtn}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        setSearchList={setSearchListHandle}
        setSearchVal={setSearchValHandle}
      />
      <Card className="dbg-tab-page__card">
        <Card.Body className="dbg-tab-page__card-body">
          {/* <div className="dbg-pipeline-change-log-change-detail__topbar" ref={filterHeightRef}>
          </div> */}
          <ChangeChecklistTable
            loading={changeChecklistsLoading}
            error={changeChecklistsError}
            isSearch={isSearch}
            filter={filter}
            pagination={pagination}
            total={changeChecklistsTotal}
            data={changeChecklists}
            sort={sort}
            onSortChange={handleSortChange}
            onPaginationChange={handlePaginationChange}
            onRetry={handleRetry}
            onReset={handleReset}
            tableHeight={tableHeight}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChangeChecklist;
